import axios, {
  AxiosInstance,
  AxiosProgressEvent,
  AxiosRequestConfig,
  RawAxiosRequestConfig,
} from 'axios';
import axiosRetry from 'axios-retry';

export type FileUpload = {
  file: File;
  getUrl?: string | undefined;
  uploadUrl?: string | undefined;
  progress: number;
  uploadLatency: number;
  completed: boolean;
  errorOccurred: boolean;
  errorMessage: string;
  emitMetrics?: boolean;
  exposeError?: boolean;
};

type FileUploadClientProps = {
  onUpdate: (fileUpload: FileUpload) => void;
};

// We only need one AxiosInstance for any number of FileUploadClient instances
const axiosClient: AxiosInstance = Object.freeze(axios.create());

delete axios.defaults.headers.put['Content-Type'];

/** FileUploadClient can manage file uploads to any arbitrary document store. In most cases, any Opendoor S3 instance */
// TODO: Stick this in shared-fe at a future date
export class FileUploadClient {
  private axiosClient: AxiosInstance = axiosClient;
  private props: FileUploadClientProps;

  /** Constructor */
  constructor(props: FileUploadClientProps) {
    this.props = props;
  }

  /** Handle single part uploads */
  singlePartUpload = async (
    fileUpload: FileUpload,
    url: string,
    axiosConfig: AxiosRequestConfig,
  ) => {
    const uploadStartTime = Date.now();

    if (!url || !fileUpload.file || !fileUpload.uploadUrl) {
      this.props.onUpdate({
        ...fileUpload,
        emitMetrics: true,
        errorOccurred: true,
        errorMessage: `Single part upload error, no URL or file`,
      });
      return;
    }

    const response = await this.axiosClient.put(
      url ?? fileUpload.uploadUrl,
      fileUpload.file,
      axiosConfig,
    );
    if (!response?.status || response?.status !== 200) {
      if (!fileUpload.errorMessage) {
        fileUpload.emitMetrics = true;
        fileUpload.errorOccurred = true;
        fileUpload.errorMessage = `Single part upload error, status code ${
          response?.status ?? 'unknown'
        }, body: ${response?.data?.body ?? 'no response body'}`;
      }
      this.props.onUpdate({
        ...fileUpload,
      });
      return;
    }
    const elapsedMillis = Date.now() - uploadStartTime;
    this.props.onUpdate({
      ...fileUpload,
      progress: 100,
      emitMetrics: true,
      uploadLatency: elapsedMillis,
      completed: true,
    });
    return;
  };

  /** Handle the upload process  */
  uploadFile = async (fileUpload: FileUpload, url: string) => {
    axiosRetry(axios, {
      /** Establishes the retry conditions */
      retryCondition: (error) => {
        return (
          !error.response ||
          (error.response.status >= 500 && error.response.status <= 599) ||
          error.response.status == 400 ||
          error.response.status == 0
        );
      },
      // Per https://www.npmjs.com/package/axios-retry: "Unless shouldResetTimeout is set,
      //   the plugin interprets the request timeout as a global value, so it is not used for each retry
      //   but for the whole request lifecycle."
      // The intention here is to handle transient network issues causing timeouts, and to ensure retries
      //    do not immediately timeout.
      shouldResetTimeout: true,
      /** Adds an exponential retry */
      retryDelay: (retryCount) => {
        return Math.pow(2, retryCount) * 1000;
      },
    });

    this.axiosClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        fileUpload.errorMessage = error;
        fileUpload.emitMetrics = true;
        fileUpload.errorOccurred = true;
        return error;
      },
    );

    fileUpload.exposeError = true;

    let completedBytes = 0;
    let maxObservedBytes = 0;
    const axiosConfig: RawAxiosRequestConfig = {
      /** Handles the upload progress events */
      onUploadProgress: (progressEvent: AxiosProgressEvent) => {
        if (progressEvent.loaded === progressEvent.total) {
          completedBytes = completedBytes + progressEvent.total;
        }
        maxObservedBytes = Math.max(maxObservedBytes, completedBytes + progressEvent.loaded);
        this.props.onUpdate({
          ...fileUpload,
          emitMetrics: false,
          progress: Math.min(Math.round((maxObservedBytes / fileUpload.file.size) * 100), 100),
        });
      },
      headers: {
        'Content-Type': fileUpload.file?.type,
      },
    };

    this.axiosClient.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        fileUpload.errorMessage = error;
        fileUpload.emitMetrics = true;
        fileUpload.errorOccurred = true;
        return error;
      },
    );
    this.singlePartUpload(fileUpload, url, axiosConfig);
  };
}
